import { Checkbox, Dropdown, IDropdownOption, TextField } from "@fluentui/react";
import React, { useContext } from "react";
import styled from "styled-components";
import { Flex } from "../components-utils";
import { capitalize } from "../utils/text";
import { useNumberFormat } from "../utils/use-number-format/useNumberFormat";
import { ColumnOptionsContext } from "./ColumnOptions";
import {
  Column,
  NumberFilter as NumberFilterType,
  NumberFilterOperator,
  numberFilterOperators,
} from "./types";

type Props = {
  column: Column;
};

export const NumberFilter = ({ column }: Props) => {
  const filter = column.filter as NumberFilterType;
  const { setFilter, strings, id } = useContext(ColumnOptionsContext);

  const showCheckboxes =
    filter.operator !== "eq" || filter.operator === ("between" as any);

  const options: IDropdownOption[] = numberFilterOperators.map((o) => ({
    key: o,
    text: strings[`numberFilterOperator${capitalize(o)}Label`],
  }));

  return (
    <Container>
      <Dropdown
        selectedKey={filter.operator}
        options={options}
        id={id ? `${id}-numberPicker-${column.key}` : undefined}
        onChange={(_, option) => {
          if (!option) return;
          setFilter(column.key, {
            ...filter,
            operator: option.key as NumberFilterOperator,
          });
        }}
      />
      {showCheckboxes && (
        <Flex>
          {filter.operator !== "eq" && (
            <Checkbox
              label={strings.numberFilterIncludeLabel}
              checked={filter.includeNumber1}
              onChange={(_, checked) => {
                setFilter(column.key, {
                  ...filter,
                  includeNumber1: checked === true,
                });
              }}
            />
          )}
          {filter.operator === "between" && (
            <ReverseCheckbox
              label={strings.numberFilterIncludeLabel}
              checked={filter.includeNumber2}
              id={id ? `${id}-reverseCheckBox-${column.key}` : undefined}
              onChange={(_, checked) => {
                setFilter(column.key, {
                  ...filter,
                  includeNumber2: checked === true,
                });
              }}
            />
          )}
        </Flex>
      )}
      <NumberGrid>
        <NumberField
          value={filter.number1 !== null ? filter.number1.toString() : ""}
          onChange={(value) => {
            setFilter(column.key, {
              ...filter,
              number1: (value === "" ? null : value) as any,
            });
          }}
          id={id ? `${id}-numberFilter1-${column.key}` : undefined}
          {...filter}
        />
        {filter.operator === "between" && (
          <NumberField
            value={filter.number2 !== null ? filter.number2.toString() : ""}
            onChange={(value) => {
              setFilter(column.key, {
                ...filter,
                number2: (value === "" ? null : value) as any,
              });
            }}
            id={id ? `${id}-numberFilter2-${column.key}` : undefined}
            {...filter}
          />
        )}
      </NumberGrid>
      <FormulaText>
        {filter.operator === "gt" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `${filter.includeNumber1 ? ">=" : ">"} ${filter.number1}`}
        {filter.operator === "lt" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `${filter.includeNumber1 ? "<=" : "<"} ${filter.number1}`}
        {filter.operator === "eq" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          `= ${filter.number1}`}
        {filter.operator === "between" &&
          filter.number1 !== null &&
          filter.number1 !== undefined &&
          filter.number2 !== null &&
          filter.number2 !== undefined &&
          `${filter.includeNumber1 ? ">=" : ">"} ${filter.number1} ${
            strings.numberFilterAndLabel
          } ${filter.includeNumber2 ? "<=" : "<"} ${filter.number2}`}
      </FormulaText>
    </Container>
  );
};

const Container = styled.div`
  height: 180px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NumberGrid = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: 5px;
`;

const FormulaText = styled.div`
  color: ${(p) => p.theme.palette.neutralPrimary};
  font-style: italic;
  margin-top: auto;
  font-weight: bold;
`;

const ReverseCheckbox = styled(Checkbox)`
  margin-left: auto;
  .ms-Checkbox-label {
    flex-direction: row-reverse;
  }
  .ms-Checkbox-text {
    margin: 0;
    margin-right: 4px;
  }
  .ms-Checkbox-checkbox {
    margin: 0;
    margin-left: 4px;
  }
`;

const NumberField = ({
  value,
  onChange,
  max,
  placeholder,
  isNegativeAllowed = false,
  includeThousandSeparators,
  format,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  max?: number;
  placeholder?: string;
  isNegativeAllowed?: boolean;
  includeThousandSeparators?: boolean;
  format?: "integer" | "float";
  id?: string;
}) => {
  const { value: _value, onChange: _onChange } = useNumberFormat({
    value,
    setValue: (value: string) => onChange(value),
    isNegativeAllowed,
    max,
    includeThousandSeparators,
    format,
  });
  return (
    <TextField
      id={id}
      value={_value}
      onChange={(_, newValue) => {
        _onChange({ target: { value: newValue } } as any);
      }}
      placeholder={placeholder}
    />
  );
};
